import { datadogRum } from "@datadog/browser-rum";
import { datadogLogs } from "@datadog/browser-logs";
import { reactPlugin } from "@datadog/browser-rum-react";
import { createBrowserRouter as createReactRouter } from "react-router-dom";
import { createBrowserRouter as createDatadogRouter } from "@datadog/browser-rum-react/react-router-v6";
import {
  datadogEnv,
  isDevelopment,
  datadogApplicationId,
  datadogClientToken,
  datadogSite,
} from "./environment";

const DATADOG_SERVICE = import.meta.env.VITE_DATADOG_SERVICE;

let datadogInitialized = false;

const getReleaseVersion = (): string => {
  const v = import.meta.env.VITE_BUILD_NUMBER;
  return typeof v === "string" && v.trim().length > 0 ? v.trim() : "0";
};

const canInitDatadog = (): boolean =>
  !isDevelopment &&
  Boolean(datadogApplicationId) &&
  Boolean(datadogClientToken);

/** Credentials present (e.g. ErrorBoundary); unlike init, may be true in development. */
export const isDatadogEnabled = (): boolean =>
  Boolean(datadogApplicationId && datadogClientToken);

export const datadogRumSetup = (): void => {
  if (datadogInitialized || !canInitDatadog()) return;
  datadogInitialized = true;
  const version = getReleaseVersion();

  datadogLogs.init({
    clientToken: datadogClientToken,
    site: datadogSite,
    service: DATADOG_SERVICE,
    env: datadogEnv,
    version,
    forwardErrorsToLogs: true,
    forwardConsoleLogs: ["error", "warn"],
    sessionSampleRate: 100,
  });

  datadogRum.init({
    applicationId: datadogApplicationId,
    clientToken: datadogClientToken,
    site: datadogSite,
    service: DATADOG_SERVICE,
    env: datadogEnv,
    version,
    sessionSampleRate: 50,
    sessionReplaySampleRate: 0,
    trackResources: true,
    trackUserInteractions: true,
    trackLongTasks: true,
    defaultPrivacyLevel: "mask-user-input",
    usePartitionedCrossSiteSessionCookie: true,
    plugins: [reactPlugin({ router: true })],
  });

  // Temporary marker to inspect effective tags in RUM Explorer.
  datadogRum.addAction("dd_boot_config", {
    env: datadogEnv,
    service: DATADOG_SERVICE,
    version,
  });
};

export const getCreateBrowserRouter = () =>
  isDatadogEnabled() && !isDevelopment
    ? createDatadogRouter
    : createReactRouter;
